import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { CaretDown, FloppyDisk, Question } from "@phosphor-icons/react";

/** Settings-only building blocks; colors, typography and spacing come from the app theme. */
export function SettingsHelp({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={
        <Box sx={{ maxWidth: 280 }}>
          <Typography level="title-sm" sx={{ color: "inherit" }}>
            {title}
          </Typography>
          <Typography level="body-sm" sx={{ color: "inherit" }}>
            {children}
          </Typography>
        </Box>
      }
    >
      <IconButton
        size="sm"
        variant="plain"
        color="neutral"
        aria-label={`Help: ${title}`}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
        sx={{ "--IconButton-size": "28px" }}
      >
        <Question size={18} />
      </IconButton>
    </Tooltip>
  );
}

export function SettingsSection({
  title,
  description,
  children,
  defaultOpen = false,
  icon,
}: {
  title: string;
  description: string;
  children: ReactNode;
  defaultOpen?: boolean;
  icon?: ReactNode;
}) {
  return (
    <Card
      component="details"
      variant="outlined"
      open={defaultOpen || undefined}
      sx={{
        p: 0,
        gap: 0,
        minWidth: 0,
        "&[open] > summary .settings-caret": { transform: "rotate(180deg)" },
      }}
    >
      <Box
        component="summary"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2.5,
          cursor: "pointer",
          listStyle: "none",
          "&::-webkit-details-marker": { display: "none" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.400",
            outlineOffset: 2,
            borderRadius: "md",
          },
        }}
      >
        {icon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-md">{title}</Typography>
          <Typography level="body-sm" textColor="text.secondary">
            {description}
          </Typography>
        </Box>
        <CaretDown size={18} className="settings-caret" />
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5, minWidth: 0 }}>{children}</Box>
    </Card>
  );
}

export function SettingsSaveBar({
  dirty,
  saving,
  onSave,
  label = "Save changes",
  disabled = false,
  children,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  label?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState<{
    left: number;
    width: number;
    height: number;
  } | null>(null);
  useLayoutEffect(() => {
    // The existing app wrapper can grow taller than the viewport, so a sticky
    // footer follows that wrapper instead of staying on screen. Measure only
    // this settings section to keep the action visible without changing layout.
    const measure = () => {
      const host = hostRef.current?.getBoundingClientRect();
      const height = barRef.current?.getBoundingClientRect().height;
      if (!host?.width || !height) return;
      setBounds((previous) =>
        previous?.left === host.left &&
        previous.width === host.width &&
        previous.height === height
          ? previous
          : { left: host.left, width: host.width, height },
      );
    };
    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    if (hostRef.current) observer?.observe(hostRef.current);
    if (barRef.current) observer?.observe(barRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  return (
    <Box
      ref={hostRef}
      sx={{ mt: 2, minHeight: bounds ? bounds.height + 16 : undefined }}
    >
      <Card
        ref={barRef}
        variant="outlined"
        sx={{
          position: bounds ? "fixed" : "relative",
          bottom: bounds ? 12 : undefined,
          left: bounds?.left,
          width: bounds?.width,
          boxSizing: "border-box",
          zIndex: 3,
          boxShadow: "md",
          p: 2,
          // Leave room for the application's existing help button.
          pr: 8,
          bgcolor: "background.surface",
        }}
      >
        <Stack
          direction="row"
          gap={1.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography level="title-sm">
              {saving
                ? "Saving…"
                : dirty
                  ? "Unsaved changes"
                  : "All changes saved"}
            </Typography>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>{children}</Box>
          </Box>
          <Button
            startDecorator={<FloppyDisk size={18} />}
            loading={saving}
            disabled={disabled || !dirty || saving}
            onClick={onSave}
          >
            {label}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
